import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePricingMutations } from "../../../hooks/usePricing";
import {
  CategoryPricing,
  CreateCategoryPricingRequest,
  UpdateCategoryPricingRequest,
} from "../../../types/admin";

interface CategoryPricingModalProps {
  open: boolean;
  mode: "create" | "edit";
  category?: CategoryPricing;
  onClose: () => void;
  onSuccess: () => void;
}

const CategoryPricingModal: React.FC<CategoryPricingModalProps> = ({
  open,
  mode,
  category,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateCategoryPricingRequest>({
    categoryName: "",
    pricingType: "Fixed",
    fixedPrice: undefined,
    pricePerKg: undefined,
    pricePerSize: undefined,
    description: "",
    isActive: true,
    zincKeywords: "",
    isDefault: false,
    basePrice: undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createCategoryPricing, updateCategoryPricing } =
    usePricingMutations();

  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    if (open) {
      if (isEditMode && category) {
        setFormData({
          categoryName: category.categoryName,
          pricingType: category.pricingType,
          fixedPrice: category.fixedPrice || undefined,
          pricePerKg: category.pricePerKg || undefined,
          pricePerSize: category.pricePerSize || undefined,
          description: category.description || "",
          isActive: category.isActive,
          zincKeywords: category.zincKeywords || "",
          isDefault: category.isDefault ?? false,
          basePrice: category.basePrice || undefined,
        });
      } else {
        setFormData({
          categoryName: "",
          pricingType: "Fixed",
          fixedPrice: undefined,
          pricePerKg: undefined,
          pricePerSize: undefined,
          description: "",
          isActive: true,
          zincKeywords: "",
          isDefault: false,
          basePrice: undefined,
        });
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, mode, category, isEditMode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.categoryName.trim()) {
      newErrors.categoryName = t("admin.categoryNameRequired");
    }

    if (!formData.pricingType) {
      newErrors.pricingType = t("admin.pricingTypeRequired");
    }

    // Validate pricing fields based on type
    switch (formData.pricingType) {
      case "Fixed":
        if (!formData.fixedPrice || formData.fixedPrice <= 0) {
          newErrors.fixedPrice = t("admin.fixedPriceMustBeGreaterThanZero");
        }
        break;
      case "PerKg":
        if (!formData.pricePerKg || formData.pricePerKg <= 0) {
          newErrors.pricePerKg = t("admin.pricePerKgMustBeGreaterThanZero");
        }
        break;
      case "PerSize":
        if (!formData.pricePerSize || formData.pricePerSize <= 0) {
          newErrors.pricePerSize = t("admin.pricePerSizeMustBeGreaterThanZero");
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (
    field: keyof CreateCategoryPricingRequest,
    value: any,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (isCreateMode) {
        await createCategoryPricing.mutateAsync(formData);
      } else if (isEditMode && category) {
        const updateData: UpdateCategoryPricingRequest = {
          ...formData,
          fixedPrice: formData.fixedPrice || undefined,
          pricePerKg: formData.pricePerKg || undefined,
          pricePerSize: formData.pricePerSize || undefined,
        };
        await updateCategoryPricing.mutateAsync({
          id: category.id,
          data: updateData,
        });
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving category pricing:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading =
    isSubmitting ||
    createCategoryPricing.isPending ||
    updateCategoryPricing.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isCreateMode
          ? t("admin.createCategoryPricingRule")
          : t("admin.editCategoryPricingRule")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.categoryName")}
                value={formData.categoryName}
                onChange={(e) =>
                  handleFieldChange("categoryName", e.target.value)
                }
                error={!!errors.categoryName}
                helperText={errors.categoryName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.pricingType}>
                <InputLabel>{t("admin.pricingType")}</InputLabel>
                <Select
                  value={formData.pricingType}
                  onChange={(e) =>
                    handleFieldChange("pricingType", e.target.value)
                  }
                  label={t("admin.pricingType")}
                >
                  <MenuItem value="Fixed">{t("admin.fixedPrice")}</MenuItem>
                  <MenuItem value="PerKg">{t("admin.pricePerKg")}</MenuItem>
                  <MenuItem value="PerSize">{t("admin.pricePerSize")}</MenuItem>
                </Select>
                {errors.pricingType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.pricingType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Conditional pricing fields */}
            {formData.pricingType === "Fixed" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("admin.fixedPrice")}
                  type="number"
                  value={formData.fixedPrice || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "fixedPrice",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  error={!!errors.fixedPrice}
                  helperText={
                    errors.fixedPrice ||
                    t("admin.fixedPriceCategoryHelperText")
                  }
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  required
                />
              </Grid>
            )}

            {formData.pricingType === "PerKg" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("admin.pricePerKg")}
                  type="number"
                  value={formData.pricePerKg || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "pricePerKg",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  error={!!errors.pricePerKg}
                  helperText={
                    errors.pricePerKg || t("admin.pricePerKgHelperText")
                  }
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  required
                />
              </Grid>
            )}

            {formData.pricingType === "PerSize" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("admin.pricePerSize")}
                  type="number"
                  value={formData.pricePerSize || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "pricePerSize",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  error={!!errors.pricePerSize}
                  helperText={
                    errors.pricePerSize || t("admin.pricePerSizeHelperText")
                  }
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("admin.description")}
                value={formData.description}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
                multiline
                rows={3}
                helperText={t("admin.optionalPricingRuleDescription")}
              />
            </Grid>

            {/* Zinc Keywords */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.zincKeywords", "Zinc Keywords")}
                value={formData.zincKeywords || ""}
                onChange={(e) =>
                  handleFieldChange("zincKeywords", e.target.value)
                }
                helperText={t(
                  "admin.zincKeywordsHelperText",
                  "Comma-separated keywords, e.g. Tablets,iPad"
                )}
                placeholder="Tablets,iPad"
              />
            </Grid>

            {/* Base Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.basePrice", "Base Price")}
                type="number"
                value={formData.basePrice ?? ""}
                onChange={(e) =>
                  handleFieldChange(
                    "basePrice",
                    e.target.value !== "" ? parseFloat(e.target.value) : undefined
                  )
                }
                helperText={t(
                  "admin.basePriceHelperText",
                  "Optional fallback base price for this category"
                )}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleFieldChange("isActive", e.target.checked)
                    }
                  />
                }
                label={t("common.active")}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) =>
                      handleFieldChange("isDefault", e.target.checked)
                    }
                  />
                }
                label={t("admin.isDefault", "Is Default")}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t("common.saving") : isCreateMode ? t("common.create") : t("common.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryPricingModal;
